import React, { useCallback, useContext, useMemo, useState } from 'react';
import _ from 'lodash';
import { Modifiers } from 'src/api/types';
import {
  AppLocation,
  HeaderData,
  LinkControlsToScenarioModalTab,
  LocationPath,
  WizardRecomendedControlsType,
  WizzardWorkspaceType,
} from './types';

type LocationNames = keyof AppLocation['paths'];
type LinkControlsModalState = { activeTab: LinkControlsToScenarioModalTab } | true | null;

interface AppContextInterface {
  location: AppLocation;
  wizardWorkspace: null | WizzardWorkspaceType;
  wizardRecomendedControls: null | WizardRecomendedControlsType;
  copiedModifiers: Modifiers | null;
  hiddenSuggestionKeys: string[];
  modals: {
    isLinkControlsToScenarioModalOpen: LinkControlsModalState;
  };
  setLocationHeader: (header: HeaderData[]) => void;
  /** Merges into the existing paths — same semantics as the old Redux reducer. */
  setLocationPaths: (paths: Partial<{ [key in LocationNames]: LocationPath }>) => void;
  setWizardWorkspace: (value: null | WizzardWorkspaceType) => void;
  setWizardRecomendedControls: (value: null | WizardRecomendedControlsType) => void;
  setCopiedModifiers: (value: Modifiers | null) => void;
  setHiddenSuggestionKeys: (value: string[]) => void;
  setLinkControlsToScenarioModal: (value: LinkControlsModalState) => void;
}

const AppContext = React.createContext<AppContextInterface | undefined>(undefined);

export const AppProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [location, setLocation] = useState<AppLocation>({ header: [], paths: {} });
  const [wizardWorkspace, setWizardWorkspace] = useState<null | WizzardWorkspaceType>(null);
  const [wizardRecomendedControls, setWizardRecomendedControls] = useState<null | WizardRecomendedControlsType>(null);
  const [copiedModifiers, setCopiedModifiers] = useState<Modifiers | null>(null);
  const [hiddenSuggestionKeys, setHiddenSuggestionKeys] = useState<string[]>([]);
  const [isLinkControlsToScenarioModalOpen, setLinkControlsToScenarioModal] = useState<LinkControlsModalState>(null);

  const setLocationHeader = useCallback((header: HeaderData[]) => {
    // Deep-equal no-op guard: consumers previously used `useStateSelector(..., isEqual)`
    // to skip re-renders when an identical header is re-dispatched on route changes.
    setLocation((prev) => (_.isEqual(prev.header, header) ? prev : { ...prev, header }));
  }, []);

  const setLocationPaths = useCallback((paths: Partial<{ [key in LocationNames]: LocationPath }>) => {
    setLocation((prev) => ({ ...prev, paths: { ...prev.paths, ...paths } }));
  }, []);

  const value = useMemo(
    () => ({
      location,
      wizardWorkspace,
      wizardRecomendedControls,
      copiedModifiers,
      hiddenSuggestionKeys,
      modals: { isLinkControlsToScenarioModalOpen },
      setLocationHeader,
      setLocationPaths,
      setWizardWorkspace,
      setWizardRecomendedControls,
      setCopiedModifiers,
      setHiddenSuggestionKeys,
      setLinkControlsToScenarioModal,
    }),
    [
      location,
      wizardWorkspace,
      wizardRecomendedControls,
      copiedModifiers,
      hiddenSuggestionKeys,
      isLinkControlsToScenarioModalOpen,
      setLocationHeader,
      setLocationPaths,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppState = (): AppContextInterface => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppProvider');
  }
  return context;
};
